import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { Play, Save } from 'lucide-react'
import { LLMsListItem } from '@intone/shared'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCreateChatbotTest,
  useUpdateChatbotTest,
  useTriggerTestRun,
  useLlmTools,
  type ChatbotTest,
} from '@/hooks/useSimulations'
import { LastTestResult } from './LastTestResult'
import { RunHistory } from './RunHistory'

interface TestEditorProps {
  test: ChatbotTest | null
  llms: LLMsListItem[]
  defaultLlmId: number | null
  isNew: boolean
  onChange: (hasChanges: boolean) => void
  onSaveSuccess: (testId: number) => void
}

export function TestEditor({ test, llms, defaultLlmId, isNew, onChange, onSaveSuccess }: TestEditorProps) {
  const createTest = useCreateChatbotTest()
  const updateTest = useUpdateChatbotTest()
  const triggerRun = useTriggerTestRun()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [customerPrompt, setCustomerPrompt] = useState('')
  const [evaluationPrompt, setEvaluationPrompt] = useState('')
  const [maxTurns, setMaxTurns] = useState(5)
  const [startingAgent, setStartingAgent] = useState('agent')
  const [hasInterruption, setHasInterruption] = useState(false)
  const [mockTools, setMockTools] = useState<Record<string, string>>({})
  const [runLlmId, setRunLlmId] = useState(defaultLlmId)

  useEffect(() => { setRunLlmId(defaultLlmId) }, [defaultLlmId])

  const { data: llmTools = [] } = useLlmTools(runLlmId)

  const testId = test?.id ?? null
  useEffect(() => {
    if (test && !isNew) {
      setName(test.name)
      setDescription(test.description || '')
      setCustomerPrompt(test.customer_prompt)
      setEvaluationPrompt(test.evaluation_prompt)
      setMaxTurns(test.max_turns)
      setStartingAgent(test.starting_agent)
      setHasInterruption(test.has_interruption ?? false)
      setMockTools((test.mock_tools as Record<string, string>) ?? {})
    } else {
      setName('')
      setDescription('')
      setCustomerPrompt('')
      setEvaluationPrompt('')
      setMaxTurns(5)
      setStartingAgent('agent')
      setHasInterruption(false)
      setMockTools({})
    }
  }, [testId, isNew]) // eslint-disable-line react-hooks/exhaustive-deps

  const checkChanges = useCallback(() => {
    if (isNew) {
      onChange(name.length > 0 || customerPrompt.length > 0 || evaluationPrompt.length > 0)
      return
    }
    if (!test) return
    onChange(
      name !== test.name ||
      description !== (test.description || '') ||
      customerPrompt !== test.customer_prompt ||
      evaluationPrompt !== test.evaluation_prompt ||
      maxTurns !== test.max_turns ||
      startingAgent !== test.starting_agent ||
      hasInterruption !== (test.has_interruption ?? false) ||
      JSON.stringify(mockTools) !== JSON.stringify((test?.mock_tools as Record<string, string>) ?? {})
    )
  }, [test, isNew, name, description, customerPrompt, evaluationPrompt, maxTurns, startingAgent, hasInterruption, mockTools, onChange])

  useEffect(() => { checkChanges() }, [checkChanges])

  const handleSave = async () => {
    if (!name.trim() || !customerPrompt.trim() || !evaluationPrompt.trim()) {
      toast.error('Name, customer prompt, and evaluation prompt are required')
      return
    }

    const data = {
      name: name.trim(),
      description: description.trim() || undefined,
      customer_prompt: customerPrompt.trim(),
      evaluation_prompt: evaluationPrompt.trim(),
      max_turns: maxTurns,
      starting_agent: startingAgent,
      has_interruption: hasInterruption,
      mock_tools: Object.keys(mockTools).length > 0 ? mockTools : undefined,
    }

    try {
      if (isNew) {
        const result = await createTest.mutateAsync(data)
        toast.success('Test created')
        onSaveSuccess(result.id)
      } else if (test) {
        await updateTest.mutateAsync({ id: test.id, ...data })
        toast.success('Test saved')
        onChange(false)
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save test')
    }
  }

  const handleRunTest = async () => {
    if (!test || !runLlmId) return
    try {
      // Save before running
      await handleSave()
      await triggerRun.mutateAsync({ testId: test.id, llmId: runLlmId })
      toast.success('Test run started')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start test run')
    }
  }

  const isSaving = createTest.isPending || updateTest.isPending
  const isRunStarting = triggerRun.isPending || isSaving

  return (
    <div className="space-y-4">
      {/* Editor card */}
      <Card className="border-gray-300">
        <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-body font-semibold">
              {isNew ? 'New Test' : test?.name ?? 'Edit Test'}
            </CardTitle>
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              <Save className="w-3 h-3 mr-2" />
              {isSaving ? 'Saving...' : isNew ? 'Create' : 'Save'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-detail text-gray-500">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Test name" className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-detail text-gray-500">Description</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" className="h-8 text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-detail text-gray-500">Customer Prompt</Label>
              <Textarea
                value={customerPrompt}
                onChange={(e) => setCustomerPrompt(e.target.value)}
                placeholder="Prompt that simulates the customer..."
                className="min-h-[300px] font-mono resize-y"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-detail text-gray-500">Evaluation Prompt</Label>
              <Textarea
                value={evaluationPrompt}
                onChange={(e) => setEvaluationPrompt(e.target.value)}
                placeholder="Prompt that evaluates the conversation..."
                className="min-h-[300px] font-mono resize-y"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div className="space-y-1">
              <Label className="text-detail text-gray-500">Max Turns</Label>
              <Input
                type="text"
                value={maxTurns || ''}
                onChange={(e) => {
                  const val = e.target.value
                  if (val === '') { setMaxTurns(0); return }
                  if (!/^\d+$/.test(val)) return
                  const num = parseInt(val, 10)
                  if (num > 50) return
                  setMaxTurns(num)
                }}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-detail text-gray-500">Who starts the conversation</Label>
              <Select value={startingAgent} onValueChange={setStartingAgent}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2 col-span-2">
              <Checkbox
                id="has-interruption"
                checked={hasInterruption}
                onCheckedChange={(checked) => setHasInterruption(checked === true)}
              />
              <label htmlFor="has-interruption" className="text-sm text-gray-600 cursor-pointer">
                Interruption — Allow the customer to interrupt the agent during speech
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mock Tools */}
      {llmTools.length > 0 && (
        <Card className="border-gray-300">
          <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
            <CardTitle className="text-body font-semibold">Mock Tools</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            <p className="text-xs text-gray-500">
              Enable mock responses for tools. When enabled, the test will use the configured response instead of calling the real tool.
            </p>
            {llmTools.map((tool) => {
              const isEnabled = tool.name in mockTools
              return (
                <div key={tool.id} className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`mock-tool-${tool.id}`}
                      checked={isEnabled}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setMockTools(prev => ({ ...prev, [tool.name]: '{"success": true}' }))
                        } else {
                          setMockTools(prev => {
                            const next = { ...prev }
                            delete next[tool.name]
                            return next
                          })
                        }
                      }}
                    />
                    <label htmlFor={`mock-tool-${tool.id}`} className="text-sm font-medium cursor-pointer">
                      {tool.name}
                    </label>
                  </div>
                  {isEnabled && (
                    <Textarea
                      value={mockTools[tool.name] ?? ''}
                      onChange={(e) => setMockTools(prev => ({ ...prev, [tool.name]: e.target.value }))}
                      placeholder='{"success": true, "data": "..."}'
                      className="min-h-[60px] font-mono text-xs resize-y ml-6"
                    />
                  )}
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Run section */}
      {!isNew && test && (
        <Card className="border-gray-300">
          <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
            <CardTitle className="text-body font-semibold">Run Test</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1 max-w-xs">
                <Label className="text-detail text-gray-500">LLM</Label>
                <Select value={runLlmId ? String(runLlmId) : ''} onValueChange={(v) => setRunLlmId(Number(v))}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select LLM" />
                  </SelectTrigger>
                  <SelectContent>
                    {llms.map((llm) => (
                      <SelectItem key={llm.id} value={String(llm.id)}>
                        {llm.name || `LLM #${llm.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" onClick={handleRunTest} disabled={isRunStarting || !runLlmId}>
                <Play className="w-3 h-3 mr-1.5" />
                {triggerRun.isPending ? 'Starting...' : 'Run'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Last test result */}
      {!isNew && test && <LastTestResult key={test.id} testId={test.id} maxTurns={test.max_turns} />}

      {/* Run history */}
      {!isNew && test && (
        <Card className="border-gray-300">
          <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
            <CardTitle className="text-body font-semibold">Run History</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <RunHistory testId={test.id} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}